// Temporary static seed data standing in for the live backend this preview page
// doesn't have access to yet. Only this file (and the fetch functions in
// penetrationReportV2Service.ts) should need to change when live data arrives —
// see that file's header comment for the seed-vs-live boundary.

import { Channel, ChannelType, MarketRecord, Period, Solution, SolutionType } from './types';

export const SEED_BRANDS: string[] = [
  'Walmart', 'Kroger', 'Costco', 'Wegmans', 'Whole Foods',
  'Target', 'Publix', 'Albertsons', 'Meijer', 'Aldi',
];

interface SeedState {
  name: string;
  code: string;
  lat: number;
  lng: number;
  /** Skews records toward highCrime / cartOrdinance so selecting a signal tile visibly changes the map. */
  crimeSkew: boolean;
  ordinanceSkew: boolean;
}

const SEED_STATES: SeedState[] = [
  { name: 'California', code: 'CA', lat: 36.7783, lng: -119.4179, crimeSkew: true, ordinanceSkew: true },
  { name: 'Texas', code: 'TX', lat: 31.9686, lng: -99.9018, crimeSkew: true, ordinanceSkew: false },
  { name: 'Florida', code: 'FL', lat: 27.6648, lng: -81.5158, crimeSkew: true, ordinanceSkew: false },
  { name: 'New York', code: 'NY', lat: 40.7128, lng: -74.0060, crimeSkew: true, ordinanceSkew: false },
  { name: 'Illinois', code: 'IL', lat: 40.6331, lng: -89.3985, crimeSkew: true, ordinanceSkew: false },
  { name: 'Pennsylvania', code: 'PA', lat: 41.2033, lng: -77.1945, crimeSkew: false, ordinanceSkew: false },
  { name: 'Ohio', code: 'OH', lat: 40.4173, lng: -82.9071, crimeSkew: false, ordinanceSkew: false },
  { name: 'Georgia', code: 'GA', lat: 32.1656, lng: -82.9001, crimeSkew: true, ordinanceSkew: false },
  { name: 'North Carolina', code: 'NC', lat: 35.7596, lng: -79.0193, crimeSkew: false, ordinanceSkew: false },
  { name: 'Michigan', code: 'MI', lat: 44.3148, lng: -85.6024, crimeSkew: false, ordinanceSkew: false },
  { name: 'Arizona', code: 'AZ', lat: 34.0489, lng: -111.0937, crimeSkew: false, ordinanceSkew: false },
  { name: 'Washington', code: 'WA', lat: 47.7511, lng: -120.7401, crimeSkew: false, ordinanceSkew: true },
  { name: 'Colorado', code: 'CO', lat: 39.5501, lng: -105.7821, crimeSkew: false, ordinanceSkew: true },
  { name: 'Massachusetts', code: 'MA', lat: 42.4072, lng: -71.3824, crimeSkew: false, ordinanceSkew: true },
  { name: 'Tennessee', code: 'TN', lat: 35.5175, lng: -86.5804, crimeSkew: false, ordinanceSkew: false },
  { name: 'Virginia', code: 'VA', lat: 37.4316, lng: -78.6569, crimeSkew: false, ordinanceSkew: false },
];

const ALL_CHANNELS: ChannelType[] = Object.values(Channel);
const ALL_SOLUTIONS: SolutionType[] = Object.values(Solution);

// Deterministic PRNG (mulberry32) so the seed dataset is stable across reloads,
// which matters for reviewing/screenshotting a page whose data never changes.
// Exported for reuse by the trend-series generator in penetrationReportV2Service.ts.
export function createRng(seed: number) {
  let a = seed;
  return function rng() {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick<T>(rng: () => number, items: T[]): T {
  return items[Math.floor(rng() * items.length)];
}

function sampleSubset<T>(rng: () => number, items: T[], min: number, max: number): T[] {
  const count = Math.min(items.length, min + Math.floor(rng() * (max - min + 1)));
  const pool = [...items];
  const result: T[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.floor(rng() * pool.length);
    result.push(pool.splice(idx, 1)[0]);
  }
  return result;
}

function generateSeedMarketRecords(): MarketRecord[] {
  const rng = createRng(42);
  const records: MarketRecord[] = [];
  const RECORD_COUNT = 220;

  for (let i = 0; i < RECORD_COUNT; i++) {
    const state = pick(rng, SEED_STATES);
    const brand = pick(rng, SEED_BRANDS);
    const channel = pick(rng, ALL_CHANNELS);

    // ~28% of the addressable market is an existing customer "as of" today,
    // matching the mockup's illustrative 28.1% overall penetration figure.
    const isCustomerAsOf = rng() < 0.28;
    // Most current customers were already customers a year ago (retained);
    // a small slice of non-customers were customers a year ago (churned).
    const isCustomerPrior = isCustomerAsOf ? rng() < 0.88 : rng() < 0.05;

    const solutionsInstalled = isCustomerAsOf ? sampleSubset(rng, ALL_SOLUTIONS, 1, 3) : [];
    const solutionsInstalledPrior = isCustomerPrior
      ? sampleSubset(rng, ALL_SOLUTIONS, 0, Math.max(1, solutionsInstalled.length - 1))
      : [];

    const highCrime = rng() < (state.crimeSkew ? 0.42 : 0.1);
    const cartOrdinance = rng() < (state.ordinanceSkew ? 0.4 : 0.08);
    const highPurchekConcentration = solutionsInstalled.includes(Solution.PURCHEK)
      ? rng() < 0.65
      : rng() < 0.15;

    records.push({
      id: `mkt-${i + 1}`,
      brand,
      channel,
      state: state.name,
      stateCode: state.code,
      // Jitter around the state centroid so markers don't all stack on one point.
      lat: state.lat + (rng() - 0.5) * 2.2,
      lng: state.lng + (rng() - 0.5) * 2.2,
      isCustomerAsOf,
      isCustomerPrior,
      solutionsInstalled,
      solutionsInstalledPrior,
      highCrime,
      cartOrdinance,
      highPurchekConcentration,
    });
  }

  return records;
}

export const SEED_MARKET_RECORDS: MarketRecord[] = generateSeedMarketRecords();

export const DEFAULT_AS_OF_PERIOD: Period = { year: 2026, month: 7 };
export const DEFAULT_COMPARE_PERIOD: Period = { year: 2025, month: 7 };
export const LONG_TERM_TARGET_PCT = 85;
